import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { Paper, CircularProgress, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Building2, BadgeCheck } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ModernSelect from "../ModernSelect";

// Máscaras (mesma abordagem do checkout: guarda só dígitos, formata na exibição).
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const applyMask = (digits, mask) => {
  let out = "", di = 0;
  for (const ch of mask) {
    if (di >= digits.length) break;
    if (ch === "#") out += digits[di++];
    else out += ch;
  }
  return out;
};
const fmtCnpj = (v) => applyMask(onlyDigits(v).slice(0, 14), "##.###.###/####-##");
const fmtPhone = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  return applyMask(d, d.length <= 10 ? "(##) ####-####" : "(##) #####-####");
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const border = dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
  return {
    card: {
      padding: theme.spacing(3),
      borderRadius: 16,
      maxWidth: 720,
      background: dark ? "rgba(18,18,19,0.6)" : "#fff",
      border: dark ? "1px solid rgba(91,124,255,0.15)" : "1px solid rgba(0,0,0,0.06)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2.5),
    },
    badge: {
      width: 42,
      height: 42,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(91,124,255,0.18)" : "rgba(91,124,255,0.12)",
      color: dark ? "#7c9aff" : "#3D5CFD",
      border: "1px solid rgba(91,124,255,0.3)",
    },
    title: { fontSize: "0.95rem", fontWeight: 700, color: dark ? "#f1f5f9" : "#1e293b" },
    subtitle: { fontSize: "0.8rem", color: theme.palette.text.secondary, marginTop: 1 },
    primaryRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: theme.spacing(2) },
    primaryChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: "0.72rem",
      fontWeight: 700,
      padding: "4px 9px",
      borderRadius: 7,
      background: dark ? "rgba(16,185,129,0.16)" : "rgba(16,185,129,0.1)",
      color: dark ? "#34d399" : "#059669",
      border: "1px solid rgba(16,185,129,0.3)",
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(1.75),
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    rowGap: { marginTop: theme.spacing(1.75) },
    field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
    label: { fontSize: "0.8rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    input: {
      height: 42,
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: dark ? "#f8fafc" : "#111827",
      padding: "0 12px",
      fontSize: "0.9rem",
      fontFamily: "inherit",
      width: "100%",
      boxSizing: "border-box",
      outline: "none",
      transition: "border-color 120ms",
      "&:focus": { borderColor: "#3D5CFD" },
      "&::placeholder": { color: dark ? "rgba(248,250,252,0.35)" : "#9ca3af" },
    },
    hint: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1.5),
      fontSize: "0.78rem",
      lineHeight: 1.5,
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: theme.spacing(2.5),
    },
    primaryBtn: {
      borderRadius: 8,
      border: "none",
      height: 40,
      padding: "0 22px",
      cursor: "pointer",
      fontSize: "0.88rem",
      fontWeight: 700,
      fontFamily: "inherit",
      color: "#fff",
      background: "#3D5CFD",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      transition: "opacity 120ms",
      "&:hover": { opacity: 0.92 },
      "&:disabled": { opacity: 0.6, cursor: "default" },
    },
  };
});

const emptyForm = { razaoSocial: "", nomeFantasia: "", cnpj: "", phone: "", email: "" };

const CompanyDataForm = ({ company, onSaved }) => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState(company?.id || null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fillFromCompany = useCallback((c) => {
    if (!c) { setForm(emptyForm); return; }
    setForm({
      razaoSocial: c.razaoSocial || "",
      nomeFantasia: c.nomeFantasia || "",
      cnpj: onlyDigits(c.cnpj || ""),
      phone: onlyDigits(c.phone || ""),
      email: c.email || "",
    });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/companies/group-companies");
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setCompanies(list);
        const initialId =
          company?.id && list.some((c) => c.id === company.id)
            ? company.id
            : list[0]?.id ?? null;
        setSelectedId(initialId);
        fillFromCompany(list.find((c) => c.id === initialId));
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [company, fillFromCompany]);

  const selected = companies.find((c) => c.id === selectedId) || null;

  const handleSelect = (id) => {
    setSelectedId(id);
    fillFromCompany(companies.find((c) => c.id === id));
  };

  const setField = (key, raw) =>
    setForm((f) => ({ ...f, [key]: key === "cnpj" || key === "phone" ? onlyDigits(raw) : raw }));

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = {
        razaoSocial: form.razaoSocial.trim(),
        nomeFantasia: form.nomeFantasia.trim(),
        cnpj: form.cnpj,
        phone: form.phone,
        email: form.email.trim(),
      };
      const { data } = await api.put(`/companies/${selectedId}/company-data`, payload);
      setCompanies((prev) => prev.map((c) => (c.id === selectedId ? { ...c, ...data } : c)));
      toast.success(i18n.t("settings.companyData.saved"));
      if (onSaved) onSaved(data);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_INVALID_CNPJ") {
        toast.error(i18n.t("settings.companyData.invalidCnpj"));
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.header}>
        <div className={classes.badge}>
          <Building2 size={22} strokeWidth={1.75} />
        </div>
        <div>
          <div className={classes.title}>{i18n.t("settings.companyData.title")}</div>
          <div className={classes.subtitle}>{i18n.t("settings.companyData.subtitle")}</div>
        </div>
      </div>

      {companies.length > 1 && (
        <div className={`${classes.field}`} style={{ marginBottom: 18 }}>
          <span className={classes.label}>{i18n.t("settings.companyData.accountLabel")}</span>
          <ModernSelect
            value={selectedId || ""}
            onChange={(e) => handleSelect(Number(e.target.value))}
            MenuProps={{ disableScrollLock: true }}
            formControlProps={{ fullWidth: true }}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.isBillingPrimaryInGroup ? ` · ${i18n.t("settings.companyData.primaryBadge")}` : ""}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      )}

      {selected?.isBillingPrimaryInGroup && (
        <div className={classes.primaryRow}>
          <span className={classes.primaryChip}>
            <BadgeCheck size={13} /> {i18n.t("settings.companyData.primaryBadge")}
          </span>
        </div>
      )}

      <div className={classes.grid2}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.razaoSocial")}</span>
          <input
            className={classes.input}
            value={form.razaoSocial}
            onChange={(e) => setField("razaoSocial", e.target.value)}
            placeholder={i18n.t("settings.companyData.phRazaoSocial")}
            autoComplete="off"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.nomeFantasia")}</span>
          <input
            className={classes.input}
            value={form.nomeFantasia}
            onChange={(e) => setField("nomeFantasia", e.target.value)}
            placeholder={i18n.t("settings.companyData.phNomeFantasia")}
            autoComplete="off"
          />
        </div>
      </div>

      <div className={`${classes.grid2} ${classes.rowGap}`}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.cnpj")}</span>
          <input
            className={classes.input}
            inputMode="numeric"
            value={fmtCnpj(form.cnpj)}
            onChange={(e) => setField("cnpj", e.target.value)}
            placeholder="00.000.000/0000-00"
            autoComplete="off"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.phone")}</span>
          <input
            className={classes.input}
            inputMode="numeric"
            value={fmtPhone(form.phone)}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="(11) 99999-9999"
            autoComplete="off"
          />
        </div>
      </div>

      <div className={`${classes.grid2} ${classes.rowGap}`}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.email")}</span>
          <input
            className={classes.input}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder={i18n.t("settings.companyData.phEmail")}
            autoComplete="off"
          />
        </div>
        <div />
      </div>

      {selected?.isBillingPrimaryInGroup && (
        <div className={classes.hint}>{i18n.t("settings.companyData.primaryHint")}</div>
      )}

      <div className={classes.actions}>
        <button className={classes.primaryBtn} onClick={handleSave} disabled={saving}>
          {saving && <CircularProgress size={15} color="inherit" />}
          {i18n.t("settings.companyData.save")}
        </button>
      </div>
    </Paper>
  );
};

export default CompanyDataForm;
